import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Box,
  Typography,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Field } from "formik";
import color from "../../components/utils/Colors";
import { userValidationSchema } from "../../components/schema/schema";
import { countryList, addressType } from "../../components/utils/Data";
import {
  getDataFromTable,
  insertDataInTable,
  updateByIdDataInTable,
  updateProfile,
} from "../../services/services";
import { useSnackbar } from "../../components/utils/SnackBar";
import { useUser } from "../../services/Context/UserContext";

const accordionStyle = {
  border: "solid 1px",
  borderColor: color.borderColor,
  boxShadow: 4,
  borderRadius: "12px",
  marginBottom: "5px",
};

const accordionSummaryStyle = {
  fontWeight: "bold",
  color: color.textColor,
};

const textFieldStyle = {
  marginBottom: "16px",
};

const inputLabelStyle = {
  marginBottom: "8px",
  ml: 0.5,
  color: color.textColor,
  fontWeight: "bold",
};

export default function PersonalInfo() {
  const [data, setData] = useState<any>(null);
  const { user } = useUser();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (user?.id) loadAllData();
  }, [user?.id]);

  const loadAllData = async () => {
    try {
      const [addressRes, bankRes] = await Promise.all([
        getDataFromTable("address", { userId: user?.id }),
        getDataFromTable("bankDetails", { userId: user?.id }),
      ]);
      const extractData = (res: any) => {
        const d = res?.data?.data ?? res?.data ?? res;
        return Array.isArray(d) ? d[0] : d;
      };

      const addressData = extractData(addressRes);
      const bankData = extractData(bankRes);


      setData({
        name: user?.name,
        email: user?.email,
        // phoneNumber: user?.phoneNumber,
        address: addressData,
        bankDetail: bankData,
      });
    } catch (err) {
      console.log("error:", err);
    }
  };

  const initialValues = {
    fullName: user?.name ?? "",
    email: user?.email ?? "",
    // phoneNumber: user?.phoneNumber ?? "",
    addressType: data?.address?.addressType ?? "",
    address: data?.address?.address ?? "",
    city: data?.address?.city ?? "",
    country: data?.address?.country ?? "",
    postalCode: data?.address?.postalCode ?? "",
    accountHolderName: data?.bankDetail?.accountHolderName ?? "",
    bankName: data?.bankDetail?.bankName ?? "",
    accountNumber: data?.bankDetail?.accountNumber ?? "",
    IFSC: data?.bankDetail?.IFSC ?? "",
  };
  return (
    <Box
      sx={(theme) => ({
        marginTop: "25px",
        flex: 1,
        p: "3%",
        borderRadius: "16px",
        bgcolor:
          theme.palette.mode === "light"
            ? "rgba(255, 255, 255, 0.4)"
            : "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(2px)",
      })}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          color: color.textColor,
          fontWeight: "bold",
          textAlign: "center",
          textDecoration: "underline",
          textUnderlineOffset: "4px",
          width: "fit-content",
          margin: "auto",
          padding: "4px 16px",
          borderRadius: "4px",
          mb: 4,
        }}
      >
        Edit Personal Info
      </Typography>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={userValidationSchema}
        onSubmit={() => {}}
      >
        {({ handleBlur, handleChange, touched, errors, values }) => (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {/* ───────────── Personal Details ───────────── */}
            <Accordion style={{ borderRadius: "12px" }} sx={accordionStyle}>
              <AccordionSummary
                sx={accordionSummaryStyle}
                expandIcon={<ExpandMoreIcon />}
              >
                Personal Details
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexDirection="column">
                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="fullName" sx={inputLabelStyle}>
                      Full Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="fullName"
                      id="fullName"
                      fullWidth
                      className="input-root2"
                      placeholder="Full Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.fullName ? errors.fullName : ""}
                      error={touched.fullName && Boolean(errors.fullName)}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="email" sx={inputLabelStyle}>
                      Email
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="email"
                      id="email"
                      fullWidth
                      type="email"
                      className="input-root2"
                      placeholder="Email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.email ? errors.email : ""}
                      error={touched.email && Boolean(errors.email)}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="phoneNumber" sx={inputLabelStyle}>
                      Phone No
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="phoneNumber"
                      id="phoneNumber"
                      type="number"
                      fullWidth
                      className="input-root2"
                      placeholder="Phone No"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      // helperText={touched.phoneNumber ? errors.phoneNumber : ""}
                      // error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                      InputProps={{
                        inputProps: { min: 0 },
                        sx: {
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            { display: "none" },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        },
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      background: color.firstColor,
                      borderRadius: "8px",
                    }}
                    onClick={() => {
                      updateProfile({
                        fullName: values.fullName,
                        email: values.email,
                        // phoneNumber: values.phoneNumber?.toString(),
                      })
                        .then(() => {
                          showSnackbar("Personal details updated");
                        })
                        .catch((error) => {
                          showSnackbar(
                            error?.response?.data?.message ||
                              error?.response?.data?.msg ||
                              "Failed to update",
                          );
                        });
                    }}
                  >
                    Save Personal Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ───────────── Contact Details ───────────── */}
            <Accordion sx={accordionStyle}>
              <AccordionSummary
                sx={accordionSummaryStyle}
                expandIcon={<ExpandMoreIcon />}
              >
                Contact Details
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexDirection="column">
                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="addressType" sx={inputLabelStyle}>
                      Address Type
                    </InputLabel>
                    <FormControl className="input-root2" fullWidth>
                      <Field
                        as={Select}
                        name="addressType"
                        id="addressType"
                        fullWidth
                        className="input-root2"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={
                          touched.addressType && Boolean(errors.addressType)
                        }
                        displayEmpty
                        renderValue={(selected: any) =>
                          selected ? selected : "Address Type"
                        }
                      >
                        {addressType.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="address" sx={inputLabelStyle}>
                      Address
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="address"
                      id="address"
                      fullWidth
                      className="input-root2"
                      placeholder="Address"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.address ? errors.address : ""}
                      error={touched.address && Boolean(errors.address)}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="city" sx={inputLabelStyle}>
                      City
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="city"
                      id="city"
                      fullWidth
                      className="input-root2"
                      placeholder="City"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.city ? errors.city : ""}
                      error={touched.city && Boolean(errors.city)}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="postalCode" sx={inputLabelStyle}>
                      Postal Code
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="postalCode"
                      id="postalCode"
                      fullWidth
                      type="number"
                      className="input-root2"
                      placeholder="Postal Code"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.postalCode ? errors.postalCode : ""}
                      error={touched.postalCode && Boolean(errors.postalCode)}
                      InputProps={{
                        inputProps: { min: 0 },
                        sx: {
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            { display: "none" },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="country" sx={inputLabelStyle}>
                      Country
                    </InputLabel>
                    <FormControl className="input-root2" fullWidth>
                      <Field
                        as={Select}
                        name="country"
                        id="country"
                        fullWidth
                        className="input-root2"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.country && Boolean(errors.country)}
                        displayEmpty
                        renderValue={(selected: any) =>
                          selected ? selected : "Country"
                        }
                      >
                        {countryList.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Box>

                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      background: color.firstColor,
                      borderRadius: "8px",
                    }}
                    onClick={() => {
                      const addressPayload = {
                        address: values.address,
                        addressType: values.addressType,
                        city: values.city,
                        country: values.country,
                        postalCode: values.postalCode?.toString(),
                        userId: user?.id,
                      };

                      const request = data?.address?.id
                        ? updateByIdDataInTable(
                            "address",
                            data.address.id,
                            addressPayload,
                          )
                        : insertDataInTable("address", addressPayload);

                      request
                        .then(() => {
                          showSnackbar("Address updated");
                          loadAllData(); // refresh after save
                        })
                        .catch((error) => {
                          showSnackbar(
                            error?.response?.data?.message ||
                              error?.response?.data?.msg ||
                              "Failed to update address",
                          );
                        });
                    }}
                  >
                    Save Contact Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ───────────── Bank Details ───────────── */}
            <Accordion sx={accordionStyle}>
              <AccordionSummary
                sx={accordionSummaryStyle}
                expandIcon={<ExpandMoreIcon />}
              >
                Bank Details
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexDirection="column">
                  <Box sx={textFieldStyle}>
                    <InputLabel
                      htmlFor="accountHolderName"
                      sx={inputLabelStyle}
                    >
                      Account Holder Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="accountHolderName"
                      id="accountHolderName"
                      fullWidth
                      className="input-root2"
                      placeholder="Account Holder Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={
                        touched.accountHolderName
                          ? errors.accountHolderName
                          : ""
                      }
                      error={
                        touched.accountHolderName &&
                        Boolean(errors.accountHolderName)
                      }
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="bankName" sx={inputLabelStyle}>
                      Bank Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="bankName"
                      id="bankName"
                      fullWidth
                      className="input-root2"
                      placeholder="Bank Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.bankName ? errors.bankName : ""}
                      error={touched.bankName && Boolean(errors.bankName)}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="accountNumber" sx={inputLabelStyle}>
                      Bank Account Number
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="accountNumber"
                      id="accountNumber"
                      fullWidth
                      type="number"
                      className="input-root2"
                      placeholder="Bank Account Number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={
                        touched.accountNumber ? errors.accountNumber : ""
                      }
                      error={
                        touched.accountNumber && Boolean(errors.accountNumber)
                      }
                      InputProps={{
                        inputProps: { min: 0 },
                        sx: {
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            { display: "none" },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={textFieldStyle}>
                    <InputLabel htmlFor="IFSC" sx={inputLabelStyle}>
                      IFSC Code
                    </InputLabel>
                    <Field
                      as={TextField}
                      name="IFSC"
                      id="IFSC"
                      fullWidth
                      className="input-root2"
                      placeholder="IFSC Code"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      helperText={touched.IFSC ? errors.IFSC : ""}
                      error={touched.IFSC && Boolean(errors.IFSC)}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      background: color.firstColor,
                      borderRadius: "8px",
                    }}
                    onClick={() => {
                      const bankPayload = {
                        accountHolderName: values.accountHolderName,
                        bankName: values.bankName,
                        accountNumber: values.accountNumber?.toString(),
                        IFSC: values.IFSC,
                        userId: user?.id,
                      };

                      const request = data?.bankDetail?.id
                        ? updateByIdDataInTable(
                            "bankDetails",
                            data.bankDetail.id,
                            bankPayload,
                          )
                        : insertDataInTable("bankDetails", bankPayload);

                      request
                        .then(() => {
                          showSnackbar("Bank details updated");
                          loadAllData(); // refresh after save
                        })
                        .catch((error) => {
                          showSnackbar(
                            error?.response?.data?.message ||
                              error?.response?.data?.msg ||
                              "Failed to update bank details",
                          );
                        });
                    }}
                  >
                    Save Bank Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Formik>
    </Box>
  );
}
